import {
  type Dispatch,
  type ForwardedRef,
  forwardRef,
  type SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import _, { drop, take, uniq } from "lodash";
import { useConfirmModal } from "@/components/modals/ConfirmModal";
import { ArrowDown, Close, Tick } from "../icons";
import Dropdown, { type Option as DropdownOption } from "./Dropdown";
import styles from "./Select.module.css";
import { SearchInput } from "./SearchInput";
import Button from "./Button";
import Checkbox from "./Checkbox";
import Chip from "./Chip";
import Tooltip from "./Tooltip";

export type OptionMetadata = { nextValues: string[] };
export type Option = {
  value: string;
  label: string;
  disabled?: boolean;
  render?: (option: Option, metadata: OptionMetadata) => JSX.Element;
  className?: string;
};

type CommonProps = {
  options?: readonly Option[];
  placeholder?: string;
  withSearch?: boolean;
  disabled?: boolean;
  error?: string;
  name?: string;
  icon?: JSX.Element;
  header?: React.ReactNode;
  label?: string;
  subtitle?: React.ReactNode;
  customTrigger?: (originalTrigger: React.ReactNode) => React.ReactNode;
  className?: string;
  role?: string;
  buttonClassName?: string;
  dropdownClassName?: string;
  optionsContainerClassName?: string;
  requireConfirm?: boolean;
  customApplyText?: string;
  onClickReset?: () => void;
  onClickOutside?: () => void;
  onClickApply?: () => void;
  onBeforeApply?: () => boolean;
  onOpen?: () => void;
  onClose?: () => void;
  iconOnly?: boolean;
  tabIndex?: number;
};

type SingleProps = {
  value?: string;
  multiple?: false;
  onChange?: (value: string) => unknown;
};

type MultipleProps = {
  values?: string[];
  multiple: true;
  onChange?: (values: string[]) => unknown;
  setNextValues?: Dispatch<SetStateAction<string[]>>;
  forceShowResetButtonOnFooter?: boolean;
};

export type SelectProps = CommonProps & (SingleProps | MultipleProps);

const renderMultipleLabel = (
  options: readonly Option[] = [],
  values: string[] = [],
  onClose: (option: Option) => void
) => (
  <div className={"flex gap-ds-2 flex-wrap"}>
    {take(values, 10).map((value) => {
      const option = options.find((o) => o.value === value);
      if (!option) return null;
      return (
        <Chip key={value} variant={"secondary"} size={"small"}>
          <Tooltip
            triggerContainerClassName={"flex gap-ds-2 items-center"}
            delay={1000}
            trigger={
              <>
                <span
                  className={`${
                    values.length > 1 ? `max-w-[64px]` : ``
                  } overflow-hidden text-ellipsis whitespace-nowrap`}
                >
                  {option.label}
                </span>
                <Close
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose?.(option);
                  }}
                />
              </>
            }
          >
            {option.label}
          </Tooltip>
        </Chip>
      );
    })}
    {values.length > 10 && (
      <Tooltip
        delay={1000}
        trigger={
          <Chip variant={"secondary"} size={"small"}>
            And {values.length - 10} more...
          </Chip>
        }
      >
        <div className={"flex flex-col"}>
          {drop(values, 10).map((value, i) => (
            <span key={i}>{options.find((o) => o.value === value)?.label}</span>
          ))}
        </div>
      </Tooltip>
    )}
  </div>
);

const Trigger = forwardRef(
  (
    {
      toggleOpen,
      isOpen,
      disabled,
      error,
      multiple,
      placeholder,
      options = [],
      buttonClassName,
      className,
      icon,
      iconOnly,
      customTrigger,
      ...rest
    }: SelectProps & { isOpen: boolean; toggleOpen: () => void },
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    //TODO find a better way to do this
    const { value } = rest as SingleProps;
    const { values = [], setNextValues, onChange } = rest as MultipleProps;

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (isOpen) {
        if (e.key === "Tab") {
          e.preventDefault();
          toggleOpen();
        }
      }
      switch (e.key) {
        case "Enter":
        case "ArrowDown":
        case "Escape":
          e.preventDefault();
          toggleOpen();
          break;
        default:
          break;
      }
    };

    const baseTriggerContent = multiple ? (
      values?.length > 0 ? (
        renderMultipleLabel(options, values, (option) => {
          const updatedValues = values.filter((v) => v !== option.value);
          setNextValues?.(updatedValues);
          onChange?.(updatedValues);
        })
      ) : (
        <span className={styles.value}>{placeholder}</span>
      )
    ) : value !== undefined ? (
      <span className={styles.value}>
        {options.find((o) => o.value === value)?.label}
      </span>
    ) : (
      <span className={styles.value}>{placeholder}</span>
    );

    return (
      <div ref={ref} onClick={() => !disabled && toggleOpen()} role="button">
        <div className={styles.triggerIconLeft}>{icon}</div>
        <div
          className={`
          ${buttonClassName}
          ${styles.trigger}
          ${
            value !== undefined || values?.length
              ? values?.length > 1
                ? styles.triggerWithMultipleValues
                : styles.triggerWithValue
              : ""
          }
          ${multiple ? styles.multiple : ""}
          ${isOpen ? styles.triggerOpen : ""}
          ${disabled ? styles.triggerDisabled : ""}
          ${error ? styles.triggerError : ""}
          ${icon ? styles.icon : ""}
          ${iconOnly ? styles.iconOnly : ""}
          ${className}
          `}
          onKeyDown={handleKeyDown}
          tabIndex={rest.tabIndex ?? 0}
        >
          {customTrigger
            ? customTrigger(baseTriggerContent)
            : baseTriggerContent}
        </div>
        <div className={styles.triggerIcon}>
          <ArrowDown />
        </div>
      </div>
    );
  }
);

Trigger.displayName = "Trigger";

const Header = ({
  withSearch,
  setFilterValue,
  filterValue,
  header,
  options,
  nextValues,
  onClickSelectAll,
  showSelectAll,
}: Omit<SelectProps, "options"> & {
  setFilterValue: Dispatch<SetStateAction<string>>;
  filterValue: string;
  header?: React.ReactNode;
  onClickSelectAll: (selectAll: boolean) => void;
  options: DropdownOption[];
  nextValues: string[];
  showSelectAll: boolean;
}) => {
  const nextValuesByOptions = nextValues.filter((nv) =>
    options.find((o) => o.value === nv)
  );
  const isAllSelected =
    options.length > 0 && options.length === nextValuesByOptions.length;
  return (
    <>
      {header && <div className={styles.headerText}>{header}</div>}
      {withSearch ? (
        <div className={styles.search}>
          <SearchInput
            autoFocus
            onChange={(e) => setFilterValue(e.target.value)}
            onClickClear={() => setFilterValue("")}
            value={filterValue}
            placeholder="Search"
            fullWidth
          />
        </div>
      ) : null}
      {showSelectAll && (
        <Button
          className={"w-full"}
          size={"small"}
          onClick={() => onClickSelectAll(!isAllSelected)}
          variant={"link"}
        >
          {isAllSelected ? `Deselect` : `Select`} all {` (${options.length})`}
        </Button>
      )}
    </>
  );
};

const Footer = ({
  onClickReset,
  onClickApply,
  onBeforeApply,
  onChange,
  toggleOpen,
  nextValues,
  setNextValues,
  forceShowResetButtonOnFooter,
  customApplyText,
}: Pick<
  SelectProps,
  "onClickReset" | "onClickApply" | "onBeforeApply" | "customApplyText"
> & {
  onChange?: (values: string[]) => unknown;
  toggleOpen: () => void;
  nextValues: string[];
  setNextValues: Dispatch<SetStateAction<string[]>>;
  forceShowResetButtonOnFooter?: boolean;
}) => (
  <div className={styles.footer}>
    {nextValues.length === 0 && !forceShowResetButtonOnFooter ? (
      <Button
        variant={"secondary"}
        onClick={() => {
          toggleOpen();
        }}
      >
        Cancel
      </Button>
    ) : (
      <Button
        variant={"secondary"}
        onClick={() => {
          onClickReset?.();
          setNextValues([]);
          onChange?.([]);
        }}
      >
        Reset
      </Button>
    )}
    <Button
      variant={"primary"}
      onClick={() => {
        if (onBeforeApply && !onBeforeApply()) return;
        onClickApply?.();
        onChange?.(nextValues);
        toggleOpen();
      }}
    >
      {customApplyText ?? "Apply"}
    </Button>
  </div>
);

const Select = forwardRef(
  (
    {
      disabled,
      error,
      multiple,
      options = [],
      placeholder = "Select an option",
      onChange,
      withSearch,
      className = "",
      role = "select",
      label,
      subtitle,
      header,
      name,
      icon,
      buttonClassName,
      dropdownClassName,
      optionsContainerClassName,
      onClickReset,
      onClickOutside,
      onClickApply,
      onBeforeApply,
      requireConfirm,
      iconOnly,
      ...rest
    }: SelectProps,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const { value } = rest as SingleProps;
    const { values = [], forceShowResetButtonOnFooter } = rest as MultipleProps;
    const [filterValue, setFilterValue] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [nextValues, setNextValues] = useState<string[]>([]);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const closeModal = useConfirmModal({
      title: "Close?",
      message:
        "You have pending changes that haven't been applied. Still want to leave?",
      agree: "Leave anyway",
      disagree: "Cancel",
      onAgree: () => {
        toggleOpen(false);
      },
      onClose: () => {
        setIsConfirmOpen(false);
      },
      zIndex: 400000,
      successMessage: "",
    });

    const showFooter = multiple || requireConfirm;

    const toggleOpen = useCallback(
      (forceValue?: boolean) => {
        if (multiple) {
          setNextValues(values);
        }
        setIsOpen((currentOpen) => {
          const nextValue =
            forceValue !== undefined ? forceValue : !currentOpen;
          if (nextValue) {
            rest.onOpen?.();
          }
          if (!nextValue) {
            rest.onClose?.();
          }
          return nextValue;
        });
      },
      [multiple, rest, values]
    );

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (isOpen) {
        switch (e.key) {
          case "Escape":
            e.preventDefault();
            toggleOpen();
            break;
          default:
            break;
        }
      }
    };

    const renderOption = useCallback(
      (option: Option) => {
        return (
          <>
            {multiple && !option.disabled && (
              <div className={"flex align-center"}>
                <Checkbox
                  tabIndex={-1}
                  onChange={(e) => {
                    setNextValues(
                      e.target.checked
                        ? [...nextValues, option.value]
                        : nextValues.filter((v) => v !== option.value)
                    );
                  }}
                  checked={nextValues.includes(option.value)}
                />
              </div>
            )}
            {option.render ? (
              option.render(option, { nextValues })
            ) : (
              <div className={styles.option}>
                {option.label}{" "}
                {(value === option.value ||
                  (requireConfirm && nextValues[0] === option.value)) && (
                  <div className={styles.optionSelected}>
                    <Tick />
                  </div>
                )}
              </div>
            )}
          </>
        );
      },
      [multiple, nextValues, requireConfirm, value]
    );

    const filteredOptions = useMemo<DropdownOption[]>(() => {
      const _options = options.map((o) => ({
        ...o,
        onClick: (option: any) => {
          if (multiple) {
            if (nextValues.includes(option.value)) {
              setNextValues(nextValues.filter((v) => v !== option.value));
            } else {
              setNextValues([...nextValues, option.value]);
            }
            return;
          }
          if (requireConfirm) {
            setNextValues([option.value]);
            return;
          }
          onChange?.(option.value);
          toggleOpen(false);
        },
        render: () => renderOption(o),
      })) as DropdownOption[];

      if (!filterValue) return _options;
      return _options.filter((option) =>
        option.label.toLowerCase().includes(filterValue.toLowerCase())
      );
    }, [
      filterValue,
      multiple,
      nextValues,
      onChange,
      options,
      renderOption,
      requireConfirm,
      toggleOpen,
    ]);

    const onClickSelectAll = (selectAll: boolean) => {
      if (selectAll) {
        setNextValues((v) =>
          uniq([...v, ...filteredOptions.map((o) => o.value)])
        );
      } else {
        setNextValues((v) =>
          v.filter((val) => !filteredOptions.some((o) => o.value === val))
        );
      }
    };

    const tryClose = () => {
      const pendingChanges = !_.isEqual(values, nextValues);
      const askForClose = multiple && pendingChanges;
      if (askForClose) {
        setIsConfirmOpen(true);
        closeModal.show();
      } else {
        toggleOpen(false);
      }
    };

    return (
      <div className={`flex flex-col ${className}`}>
        {label ? <label className={styles.label}>{label}</label> : null}
        {subtitle ? (
          <span className={"text-gray-400 mb-ds-1"}>{subtitle}</span>
        ) : null}
        <Dropdown
          isOpen={isOpen}
          options={filteredOptions}
          onKeyDown={handleKeyDown}
          trigger={
            multiple ? (
              <Trigger
                toggleOpen={toggleOpen}
                isOpen={isOpen}
                disabled={disabled}
                error={error}
                icon={icon}
                multiple
                values={values}
                placeholder={placeholder}
                options={options}
                buttonClassName={buttonClassName}
                setNextValues={setNextValues}
                customTrigger={rest.customTrigger}
                onChange={onChange}
                iconOnly={iconOnly}
                ref={ref}
              />
            ) : (
              <Trigger
                toggleOpen={toggleOpen}
                value={value}
                isOpen={isOpen}
                disabled={disabled}
                error={error}
                icon={icon}
                placeholder={placeholder}
                customTrigger={rest.customTrigger}
                options={options}
                buttonClassName={buttonClassName}
                iconOnly={iconOnly}
                ref={ref}
              />
            )
          }
          header={
            <Header
              withSearch={withSearch}
              filterValue={filterValue}
              setFilterValue={setFilterValue}
              onClickSelectAll={onClickSelectAll}
              header={header}
              options={filteredOptions}
              nextValues={nextValues}
              showSelectAll={filteredOptions.length > 1 && Boolean(multiple)}
            />
          }
          footer={
            showFooter ? (
              <Footer
                toggleOpen={toggleOpen}
                nextValues={nextValues}
                setNextValues={setNextValues}
                onChange={(vals) => {
                  if (multiple) {
                    onChange?.(vals);
                  } else {
                    // TODO: onChange should be (string | undefined) => unknown, but fixing it breaks too many callers
                    if (vals[0]) onChange?.(vals[0]);
                  }
                }}
                onBeforeApply={onBeforeApply}
                onClickReset={onClickReset}
                onClickApply={onClickApply}
                customApplyText={rest.customApplyText}
                forceShowResetButtonOnFooter={forceShowResetButtonOnFooter}
              />
            ) : (
              <></>
            )
          }
          className={className}
          dropdownClassName={`${
            multiple ? styles.multiple : ""
          } ${dropdownClassName}`}
          optionsClassName="ui-select-option"
          optionsContainerClassName={optionsContainerClassName}
          role={name || role}
          onClickOutside={() => {
            onClickOutside?.();
            if (!isConfirmOpen) {
              tryClose();
            }
          }}
          ref={dropdownRef}
        />
        {error && <span className={styles.errorMessage}>{error}</span>}
      </div>
    );
  }
);

Select.displayName = "Select";

export default Select;
